// Gives a bare hex code (e.g. a product catalogued with "#7B68EE" instead of
// "Purple:#7B68EE") a human-readable label. Classified by hue in HSL space rather
// than raw RGB distance to a palette — RGB-distance mis-buckets mid-tone colors
// (e.g. a medium slate blue reads as closer to gray than to blue in raw RGB terms
// even though it's clearly not a neutral color) because it ignores saturation.
const HEX_RE = /^#?[0-9a-f]{3,8}$/i;

function hexToRgb(hex) {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h;
  const num = parseInt(full, 16);
  if (Number.isNaN(num)) return null;
  return { r: (num >> 16) & 255, g: (num >> 8) & 255, b: num & 255 };
}

function hexToHsl(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const r = rgb.r / 255, g = rgb.g / 255, b = rgb.b / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return { h: h * 60, s, l };
}

function nearestColorName(hex) {
  const hsl = hexToHsl(hex);
  if (!hsl) return null;
  const { h, s, l } = hsl;
  if (s < 0.12) {
    if (l < 0.15) return 'Black';
    if (l > 0.9) return 'White';
    return 'Gray';
  }
  if (l < 0.15) return 'Black';
  if (l > 0.92) return 'White';
  if (h < 15 || h >= 345) return l > 0.72 ? 'Pink' : l < 0.35 ? 'Maroon' : 'Red';
  if (h < 45)  return l < 0.35 ? 'Brown' : 'Orange';
  if (h < 65)  return l < 0.35 ? 'Olive' : 'Yellow';
  if (h < 170) return 'Green';
  if (h < 200) return 'Teal';
  if (h < 245) return 'Blue';
  if (h < 285) return 'Purple';
  // Purple and Magenta share the same hue (~300°) in HSL — they only differ by
  // lightness (dark = Purple, mid = Magenta, light = Pink), so this band needs
  // lightness to disambiguate rather than hue alone.
  if (l < 0.35) return 'Purple';
  return l < 0.75 ? 'Magenta' : 'Pink';
}

/** Products are sometimes catalogued with a bare hex code as the color's "name"
 *  (no "Name:Hex" pair) — that raw hex then flows straight through to the order
 *  record and confirmation email. Give it a friendly approximate name instead. */
export function friendlyColorName(rawName, hex) {
  const name = (rawName || '').trim();
  if (!HEX_RE.test(name)) return name;
  return nearestColorName(hex || name) || name;
}

export const parseColors = (s) => s ? s.split(',').map(c => {
  const [name, hex] = c.trim().split(':');
  const resolvedHex = (hex || name).trim();
  return { name: friendlyColorName(name.trim(), resolvedHex), hex: resolvedHex };
}).filter(c => c.name) : [];

export const parseSizes = (s) => s ? s.split(',').map(x => x.trim()).filter(Boolean) : [];

/** The ₹ unit price checkout actually charges — mirrors the backend's
 *  BrandstorePricing.effectiveUnitPrice: the product's GST-inclusive selling price when
 *  set, else the picked variant's MRP (falling back to the product's), rounded up. */
export function unitPriceInr(product, variantId) {
  if (!product) return 0;
  if (product.marketOperatingPriceWithGst != null) return Math.ceil(Number(product.marketOperatingPriceWithGst));
  const variant = variantId != null ? product.variants?.find(v => String(v.id) === String(variantId)) : null;
  return Math.ceil(Number(variant?.mrp ?? product.mrp) || 0);
}
